import { type Observable, throwError } from 'rxjs';

import type { RestService } from '@app/shared/api/rest.service';
import type { RouteInfo } from '@app/shared/routing/router-helper';

export interface ItemResponse<T> {
    result: T;
}

export interface ListResponse<T> {
    result: T[];
    pageInfo?: { total: number };
}

export interface DevexStoreResponse<T> {
    data: T[];
    totalCount: number;
}

export interface StatusResponse {
    result: { status: string };
}

export interface LinkedItem {
    id: number;
    name?: string | null;
    objectType: string;
    projectSlug?: string | null;
    linkInfo?: RouteInfo | null;
}

export interface MoveProjectLinkedItem {
    id: number;
    objectType: string;
}

interface CrudOperations<T> {
    create(t: T): Observable<ItemResponse<T>>;
    update(t: T): Observable<ItemResponse<T>>;
    patch(t: T, showError: boolean): Observable<ItemResponse<T>>;
    getById(
        id: number,
        options: {
            auth?: boolean;
            transactionId?: number;
            [key: string]: any;
        },
    ): Observable<ItemResponse<T>>;
    getAll(params: any): Observable<ListResponse<T>>;
    getDataList(params: {
        keyword?: string;
        skip?: number;
        take?: number;
    }): Observable<ListResponse<T>>;
    getLinkedItems(id: number): Observable<ListResponse<LinkedItem>>;
    changeGroup(id: number, groupId: number | null): Observable<StatusResponse>;
    moveProject(
        id: number,
        projectSlug: string,
        linkedItems?: MoveProjectLinkedItem[],
        showError?: boolean,
    ): Observable<StatusResponse>;
    delete(id: number): Observable<StatusResponse>;
}

export abstract class WithCrudService<T extends { id?: number }> implements CrudOperations<T> {
    protected api: RestService;

    protected endpoint: string;

    /**
     * fetches item details using id if transactionId is provided gives preview URL
     */
    getById(
        id: number,
        options: {
            auth?: boolean;
            transactionId?: number;
            simple?: boolean;
            basicInfo?: boolean;
            [key: string]: any;
        } = { auth: false },
        params: { showError?: boolean } = { showError: true },
    ): Observable<ItemResponse<T>> {
        if (!Number(id))
            return throwError(
                () => new Error(`Invalid id ${id} provided for GET endpoint: ${this.endpoint}`),
            );
        if (!options.auth) {
            options.auth = null;
        }

        return this.api.getWithParams<ItemResponse<T>>(
            this.constructURL(`${this.endpoint}${id}`, options.transactionId),
            { auth: options.auth, simple: options.simple, basicInfo: options.basicInfo },
            params.showError,
            options,
        );
    }

    /**
     * Builds a preview URL based on availability of transactionId to get Endpoint to fetch data
     * for point in time for preview capability
     * @param url the endpoint from where data is fetched
     * @param transactionId snapshot ID for which state is required can be null too
     */
    constructURL(url: string, transactionId: number) {
        return transactionId ? `${url.replace(/\/$/, '')}/preview/${transactionId}` : url;
    }

    /**
     * fetches the list of items for registry pages
     * In case of versionable entity, it gets overridden with singleVersionOnly param
     */
    getDataList(params: {
        keyword?: string;
        skip?: number;
        take?: number;
    }): Observable<ListResponse<T>> {
        return this.api.getWithParams<ListResponse<T>>(this.endpoint, params);
    }

    /**
     * fetches list of items with provided parameters
     * e.g.: used in version dropdown
     */
    getAll(params = {}, options = {}): Observable<ListResponse<T>> {
        return this.api.getWithParams<ListResponse<T>>(this.endpoint, params, true, options);
    }

    /**
     * fetches objects that are linked to the current item but are not moved with it
     */
    getLinkedItems(id: number): Observable<ListResponse<LinkedItem>> {
        return this.api.get<ListResponse<LinkedItem>>(`${this.endpoint}${id}/linked_items`);
    }

    /**
     * creates an item resource
     * @param t The item to be created
     * @param showError flag to show api error in toast
     * @param options Options to pass to the API call
     */
    create(t: T, showError = true, options = {}): Observable<ItemResponse<T>> {
        // delete id since api throws 400 when id = null
        delete t.id;

        return this.api.post<ItemResponse<T>>(this.endpoint, t, showError, options);
    }

    /**
     * updates an item resource
     * @param t The item to be updated
     * @param showError flag to show api error in toast
     * @param options Options to pass to the API call
     */
    update(t: T, showError = true, options = {}): Observable<ItemResponse<T>> {
        return this.api.post<ItemResponse<T>>(`${this.endpoint}${t.id}`, t, showError, options);
    }

    /**
     * patch few fields for an item resource
     * @param t The item to be patched
     * @param showError flag to show api error in toast
     */
    patch(t: Partial<T>, showError = true): Observable<ItemResponse<T>> {
        return this.api.patch<ItemResponse<T>>(`${this.endpoint}${t.id}`, t, showError);
    }

    /**
     * changes only the group for an item resource
     */
    changeGroup(id: number, groupId: number | null, showError = true): Observable<StatusResponse> {
        return this.api.patch<StatusResponse>(
            `${this.endpoint}${id}/group`,
            { groupId },
            showError,
        );
    }

    /**
     * moves an item to another project
     */
    moveProject(
        id: number,
        projectSlug: string,
        linkedItems: MoveProjectLinkedItem[] = [],
        showError = true,
    ): Observable<StatusResponse> {
        return this.api.patch<StatusResponse>(
            `${this.endpoint}${id}/change_project`,
            { projectSlug, linkedItems },
            showError,
        );
    }

    /**
     * deletes an item resource
     * @param id The ID of the item to delete
     * @param showError flag to show api error in toast
     */
    delete(id: number, showError = true): Observable<StatusResponse> {
        return this.api.delete<StatusResponse>(`${this.endpoint}${id}`, showError);
    }

    /**
     * copies an item to create a new item and retun it
     */
    copy(id: number, projectSlug?: string | null): Observable<ItemResponse<T>> {
        return this.api.post<ItemResponse<T>>(`${this.endpoint}${id}/copy`, { projectSlug });
    }
}
